/**
 * Form Type Configuration
 * Visual styling and metadata for each form type category
 */

import {
  ClipboardCheck,
  FileText,
  UserPlus,
  Heart,
  FileCheck,
  DollarSign,
  FileEdit,
  Calendar,
  ClipboardList,
  LucideIcon
} from 'lucide-react';
import { FormType } from '@/services/api/forms';

export interface FormTypeConfig {
  value: FormType;
  label: string;
  pluralLabel: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  badgeColor: string;
  description: string;
}

export const FORM_TYPE_CONFIG: Record<FormType, FormTypeConfig> = {
  assessment: {
    value: 'assessment',
    label: 'Assessment',
    pluralLabel: 'Assessments',
    icon: ClipboardCheck,
    color: 'blue',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-700',
    textColor: 'text-blue-700 dark:text-blue-300',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
    description: 'Clinical assessments and screening tools'
  },
  progress_note: {
    value: 'progress_note',
    label: 'Progress Note',
    pluralLabel: 'Progress Notes',
    icon: FileText,
    color: 'green',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-200 dark:border-green-700',
    textColor: 'text-green-700 dark:text-green-300',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    description: 'Daily, weekly, and monthly progress documentation'
  },
  intake: {
    value: 'intake',
    label: 'Intake Form',
    pluralLabel: 'Intake Forms',
    icon: UserPlus,
    color: 'purple',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
    borderColor: 'border-purple-200 dark:border-purple-700',
    textColor: 'text-purple-700 dark:text-purple-300',
    badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
    description: 'Initial member enrollment and onboarding forms'
  },
  care_plan: {
    value: 'care_plan',
    label: 'Care Plan',
    pluralLabel: 'Care Plans',
    icon: Heart,
    color: 'red',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-200 dark:border-red-700',
    textColor: 'text-red-700 dark:text-red-300',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
    description: 'Comprehensive and individualized care plans'
  },
  consent: {
    value: 'consent',
    label: 'Consent Form',
    pluralLabel: 'Consent Forms',
    icon: FileCheck,
    color: 'yellow',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
    borderColor: 'border-yellow-200 dark:border-yellow-700',
    textColor: 'text-yellow-700 dark:text-yellow-300',
    badgeColor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
    description: 'Consent and authorization documents'
  },
  billing: {
    value: 'billing',
    label: 'Billing Form',
    pluralLabel: 'Billing Forms',
    icon: DollarSign,
    color: 'emerald',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
    borderColor: 'border-emerald-200 dark:border-emerald-700',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    badgeColor: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
    description: 'Billing, claims, and reimbursement forms'
  },
  custom: {
    value: 'custom',
    label: 'Custom Form',
    pluralLabel: 'Custom Forms',
    icon: FileEdit,
    color: 'gray',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-600',
    textColor: 'text-gray-700 dark:text-gray-300',
    badgeColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    description: 'Organization-specific custom forms'
  },
  event_detail: {
    value: 'event_detail',
    label: 'Event Detail',
    pluralLabel: 'Event Detail Forms',
    icon: Calendar,
    color: 'sky',
    bgColor: 'bg-sky-50 dark:bg-sky-900/30',
    borderColor: 'border-sky-200 dark:border-sky-700',
    textColor: 'text-sky-700 dark:text-sky-300',
    badgeColor: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
    description: 'Extended fields for Hub Meetings and Training/Workshop calendar events'
  },
  activity_log: {
    value: 'activity_log',
    label: 'Activity Log',
    pluralLabel: 'Activity Logs',
    icon: ClipboardList,
    color: 'amber',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30',
    borderColor: 'border-amber-200 dark:border-amber-700',
    textColor: 'text-amber-700 dark:text-amber-300',
    badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    description: 'Anonymous aggregate outreach activities (no member key, no PII)'
  }
};

// Helper function to get configuration for a form type
export const getFormTypeConfig = (formType: FormType): FormTypeConfig => {
  return FORM_TYPE_CONFIG[formType] || FORM_TYPE_CONFIG.custom;
};

// Array of all form types for iteration
export const ALL_FORM_TYPES: FormType[] = [
  'assessment',
  'progress_note',
  'intake',
  'care_plan',
  'consent',
  'billing',
  'custom',
  'event_detail',
  'activity_log',
];
